import {
  DifficultySettings,
  AvailableColors,
  CanvasSize,
  Cell,
  Position,
  PathSegment,
  Zone,
  WallSegment
} from '../types/maze-types';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * Параметры сложности для разных уровней
 */
export const difficultySettings: Record<string, DifficultySettings> = {
  easy: {
    cellSize: 35,
    entranceZoneSize: 1,
    exitZoneSize: 1,
    pathComplexity: 0.3,
    deadEndDensity: 0.2,
    branchingFactor: 2,
    minPathLength: 10,
    wallRemovalChance: 0.1,
    shapeStyle: 'orthogonal',
    curvature: 0.0
  },
  medium: {
    cellSize: 28,
    entranceZoneSize: 1,
    exitZoneSize: 1,
    pathComplexity: 0.5,
    deadEndDensity: 0.4,
    branchingFactor: 3,
    minPathLength: 20,
    wallRemovalChance: 0.05,
    shapeStyle: 'orthogonal',
    curvature: 0.0
  },
  hard: {
    cellSize: 22,
    entranceZoneSize: 1,
    exitZoneSize: 1,
    pathComplexity: 0.7,
    deadEndDensity: 0.6,
    branchingFactor: 4,
    minPathLength: 30,
    wallRemovalChance: 0.02,
    shapeStyle: 'orthogonal',
    curvature: 0.0
  },
  expert: {
    cellSize: 18,
    entranceZoneSize: 1,
    exitZoneSize: 1,
    pathComplexity: 0.8,
    deadEndDensity: 0.7,
    branchingFactor: 4,
    minPathLength: 40,
    wallRemovalChance: 0.01,
    shapeStyle: 'orthogonal',
    curvature: 0.0
  }
};

/**
 * Доступные цвета для рисования пути
 */
export const availableColors: AvailableColors = {
  blue: '#3B82F6',
  pink: '#EC4899',
  red: '#EF4444',
  green: '#10B981',
  yellow: '#F59E0B',
  purple: '#8B5CF6',
};

/**
 * Настройка canvas для высокого DPI (Retina дисплеи)
 * Исправляет размытость на телефонах и планшетах
 */
export function setupHighDPICanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): number {
  const dpr = window.devicePixelRatio || 1;

  // Устанавливаем внутренний размер canvas с учетом DPI
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  // CSS size: an explicit width, the height from the aspect ratio — so when the
  // container caps the width (phones) the browser scales BOTH axes and the maze
  // is never squashed (explicit width+height with max-width alone distorted it).
  canvas.style.width = `${width}px`;
  canvas.style.height = 'auto';
  canvas.style.aspectRatio = `${width} / ${height}`;

  // Масштабируем контекст для правильного рисования
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Сбрасываем трансформацию перед масштабированием
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // Применяем масштабирование для DPI
    ctx.scale(dpr, dpr);
  }

  return dpr;
}

/**
 * Расчет размеров канваса с учетом доступного пространства экрана
 */
export function calculateCanvasSize(
  windowWidth: number,
  windowHeight: number,
  headerHeight: number = 80,
  settingsPanelWidth: number = 250
): CanvasSize {
  // Учитываем оставшееся пространство для области с лабиринтом
  // На мобильном минимальные отступы (4px с каждой стороны), настройки - floating overlay
  const isMobile = windowWidth < 768;
  const availableWidth = windowWidth - (isMobile ? 8 : 40);
  const availableHeight = windowHeight - headerHeight - (isMobile ? 48 : 180);

  // На мобильном настройки - плавающая панель, не забирают место
  const maxWidth = isMobile ? availableWidth : availableWidth - settingsPanelWidth;

  // Вычисляем размеры канваса
  const canvasWidth = Math.min(maxWidth, availableHeight);
  const canvasHeight = canvasWidth; // Квадратный канвас

  return { width: canvasWidth, height: canvasHeight };
}

/**
 * Расчет количества строк и столбцов в зависимости от размера ячеек и канваса
 */
function calculateGridDimensions(
  canvasSize: CanvasSize,
  cellSize: number
): { rows: number; cols: number } {
  const cols = Math.floor(canvasSize.width / cellSize);
  const rows = Math.floor(canvasSize.height / cellSize);
  
  return { rows, cols };
}

/**
 * Проверка, что путь проходит через зоны входа и выхода и является непрерывным
 * Ключевое изменение: проверяем что КАЖДАЯ точка в сегменте связана с предыдущей БЕЗ пересечения стен
 */
export function checkMazeCompletion(
  pathSegments: PathSegment[],
  entranceZone: Zone | null,
  exitZone: Zone | null,
  cellSize: number,
  maze: Cell[][]
): boolean {
  try {
    if (!pathSegments || pathSegments.length === 0) {
      return false;
    }
    if (!exitZone || !entranceZone) {
      return false;
    }
  } catch (error) {
    logger.error('Error in checkMazeCompletion initial checks:', error);
    return false;
  }

  try {
    // Шаг 1: Собираем все точки из всех сегментов и строим граф связности МЕЖДУ ТОЧКАМИ
    // Важно: связь только если точки в соседних ячейках И нет стены между ними
    const allPoints: Array<{x: number, y: number, cellKey: string}> = [];
    const pointGraph = new Map<number, Set<number>>(); // индекс точки -> индексы связанных точек

    // Собираем все точки
    for (const segment of pathSegments) {
      for (const point of segment.points) {
        const cellX = Math.floor(point.x / cellSize);
        const cellY = Math.floor(point.y / cellSize);
        const cellKey = `${cellX},${cellY}`;
        allPoints.push({ x: point.x, y: point.y, cellKey });
      }
    }

  if (allPoints.length === 0) {
    return false;
  }

  // Строим граф связности между ТОЧКАМИ.
  //
  // Ключевой момент: во время рисования пересечение стен УЖЕ предотвращается
  // геометрически (isPathCrossingWalls / isPathCrossingCurvedWalls в MazeCanvas
  // по актуальным wallSegments). Поэтому нарисованный путь ФИЗИЧЕСКИ не может
  // проходить сквозь стену, и последовательные точки одного сегмента всегда
  // соединены — пользователь провёл их одним непрерывным движением.
  //
  // Раньше здесь заново проверялись стены по `maze`-сетке. Но rectangular-режим
  // строится через generateMazeWithShape(), который возвращает ФИКТИВНУЮ сетку
  // (все стены = true), а реальные стены живут в wallSegments. Из-за этого
  // соседние ячейки НИКОГДА не соединялись, граф рассыпался по одиночным
  // ячейкам, и решённый лабиринт не засчитывался. Соединяем последовательные
  // точки безусловно (как в checkVoronoiCompletion).
  let currentPointIndex = 0;
  for (const segment of pathSegments) {
    const segmentStartIndex = currentPointIndex;
    const segmentEndIndex = currentPointIndex + segment.points.length;

    for (let i = segmentStartIndex; i < segmentEndIndex - 1; i++) {
      if (!pointGraph.has(i)) pointGraph.set(i, new Set());
      if (!pointGraph.has(i + 1)) pointGraph.set(i + 1, new Set());
      pointGraph.get(i)!.add(i + 1);
      pointGraph.get(i + 1)!.add(i);
    }

    currentPointIndex = segmentEndIndex;
  }

  // Связываем точки из РАЗНЫХ сегментов если они достаточно близко
  // Это позволяет составлять непрерывный путь из нескольких нарисованных кусков
  const CONNECTION_THRESHOLD = cellSize * 0.6; // порог = 60% размера ячейки

  // Создаем карту: индекс точки -> индекс сегмента
  const pointToSegment = new Map<number, number>();
  let currentIdx = 0;
  for (let segIdx = 0; segIdx < pathSegments.length; segIdx++) {
    const segment = pathSegments[segIdx];
    for (let ptIdx = 0; ptIdx < segment.points.length; ptIdx++) {
      pointToSegment.set(currentIdx, segIdx);
      currentIdx++;
    }
  }

  // Для эффективности: связываем только начала/концы сегментов с ближайшими точками других сегментов
  // Это покрывает основной кейс: пользователь рисует несколько кусков пути, которые должны соединиться
  const segmentEndpoints: Array<{pointIndex: number, segmentIndex: number}> = [];
  let ptIdx2 = 0;
  for (let segIdx = 0; segIdx < pathSegments.length; segIdx++) {
    const segLen = pathSegments[segIdx].points.length;
    if (segLen > 0) {
      segmentEndpoints.push({ pointIndex: ptIdx2, segmentIndex: segIdx }); // first point
      if (segLen > 1) {
        segmentEndpoints.push({ pointIndex: ptIdx2 + segLen - 1, segmentIndex: segIdx }); // last point
      }
    }
    ptIdx2 += segLen;
  }

  for (let i = 0; i < allPoints.length; i++) {
    for (const ep of segmentEndpoints) {
      const j = ep.pointIndex;
      if (i === j) continue;
      // Only connect points from DIFFERENT segments
      if (pointToSegment.get(i) === ep.segmentIndex) continue;

      const [cell1X, cell1Y] = allPoints[i].cellKey.split(',').map(Number);
      const [cell2X, cell2Y] = allPoints[j].cellKey.split(',').map(Number);
      const cellDx = Math.abs(cell1X - cell2X);
      const cellDy = Math.abs(cell1Y - cell2Y);

      // Points must be in the same or adjacent cells
      if (cellDx > 1 || cellDy > 1) continue;

      const dx = allPoints[i].x - allPoints[j].x;
      const dy = allPoints[i].y - allPoints[j].y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance <= CONNECTION_THRESHOLD) {
        if (!pointGraph.has(i)) pointGraph.set(i, new Set());
        if (!pointGraph.has(j)) pointGraph.set(j, new Set());
        pointGraph.get(i)!.add(j);
        pointGraph.get(j)!.add(i);
      }
    }
  }

  // Шаг 2: Находим точки во входе и выходе
  const entrancePoints = new Set<number>();
  const exitPoints = new Set<number>();

  for (let i = 0; i < allPoints.length; i++) {
    const [cellX, cellY] = allPoints[i].cellKey.split(',').map(Number);

    // Проверка вхождения в зону входа
    if (cellX >= entranceZone.topLeft.x &&
        cellX < entranceZone.topLeft.x + entranceZone.size &&
        cellY >= entranceZone.topLeft.y &&
        cellY < entranceZone.topLeft.y + entranceZone.size) {
      entrancePoints.add(i);
    }

    // Проверка вхождения в зону выхода
    if (cellX >= exitZone.topLeft.x &&
        cellX < exitZone.topLeft.x + exitZone.size &&
        cellY >= exitZone.topLeft.y &&
        cellY < exitZone.topLeft.y + exitZone.size) {
      exitPoints.add(i);
    }
  }

  if (entrancePoints.size === 0 || exitPoints.size === 0) {
    return false;
  }

  // Шаг 3: Проверяем связность - есть ли путь от любой точки входа до любой точки выхода
  for (const startPoint of entrancePoints) {
    const visited = new Set<number>();
    const queue = [startPoint];
    visited.add(startPoint);

    // Проверяем, что стартовая точка вообще есть в графе
    if (!pointGraph.has(startPoint)) {
      continue;
    }

    while (queue.length > 0) {
      const current = queue.shift()!;

      // Если дошли до выхода - победа!
      if (exitPoints.has(current)) {
        return true;
      }

      // Добавляем соседей в очередь
      const neighbors = pointGraph.get(current);
      if (neighbors) {
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor)) {
            visited.add(neighbor);
            queue.push(neighbor);
          }
        }
      }
    }
  }

    return false;
  } catch (error) {
    logger.error('Error in checkMazeCompletion:', error);
    return false;
  }
}

/**
 * Проверка на пересечение пути со стенами
 * Используется для определения валидности пути пользователя
 */
export function isPathCrossingWalls(
  prevPos: Position,
  currentPos: Position,
  maze: Cell[][],
  cellSize: number
): boolean {
  // Вычисляем ячейки, в которых находятся предыдущая и текущая позиции
  const prevCellX = Math.floor(prevPos.x / cellSize);
  const prevCellY = Math.floor(prevPos.y / cellSize);
  const currCellX = Math.floor(currentPos.x / cellSize);
  const currCellY = Math.floor(currentPos.y / cellSize);
  
  // Если позиции находятся в одной ячейке, пересечений нет
  if (prevCellX === currCellX && prevCellY === currCellY) {
    return false;
  }
  
  // Если перешли в соседнюю ячейку, проверяем наличие стены между ними
  if (Math.abs(prevCellX - currCellX) + Math.abs(prevCellY - currCellY) === 1) {
    // Проверяем стены между ячейками
    
    // Если двигаемся вправо
    if (currCellX > prevCellX) {
      return maze[prevCellY]?.[prevCellX]?.walls.right || false;
    }
    
    // Если двигаемся влево
    if (currCellX < prevCellX) {
      return maze[currCellY]?.[currCellX]?.walls.right || false;
    }
    
    // Если двигаемся вниз
    if (currCellY > prevCellY) {
      return maze[prevCellY]?.[prevCellX]?.walls.bottom || false;
    }
    
    // Если двигаемся вверх
    if (currCellY < prevCellY) {
      return maze[currCellY]?.[currCellX]?.walls.bottom || false;
    }
  }
  
  // В случае диагонального движения или перехода через несколько ячеек
  // считаем, что стена пересекается (упрощение)
  return true;
}

/**
 * Получение позиции на канвасе относительно элемента
 */
function getCanvasPosition(
  event: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
  canvas: HTMLCanvasElement
): Position | null {
  const rect = canvas.getBoundingClientRect();

  // Для мыши
  if ('clientX' in event) {
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  // Для сенсорного экрана
  if (event.touches && event.touches.length > 0) {
    return {
      x: event.touches[0].clientX - rect.left,
      y: event.touches[0].clientY - rect.top,
    };
  }

  return null;
}

/**
 * Check if a line segment intersects with a quadratic Bezier curve
 * Uses sampling approach for performance
 */
function lineIntersectsBezierCurve(
  lineStart: Position,
  lineEnd: Position,
  curveStart: Position,
  curveEnd: Position,
  control: Position,
  samples: number = 20
): boolean {
  // Sample points along the Bezier curve
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const t1 = 1 - t;

    // Quadratic Bezier formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
    const x = t1 * t1 * curveStart.x + 2 * t1 * t * control.x + t * t * curveEnd.x;
    const y = t1 * t1 * curveStart.y + 2 * t1 * t * control.y + t * t * curveEnd.y;

    // Check if this point is close to the line segment
    if (pointNearLineSegment({ x, y }, lineStart, lineEnd, 2)) {
      return true;
    }
  }

  return false;
}

/**
 * Check if a point is near a line segment (within tolerance)
 */
function pointNearLineSegment(
  point: Position,
  lineStart: Position,
  lineEnd: Position,
  tolerance: number
): boolean {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    // Line segment is actually a point
    const dist = Math.sqrt(
      (point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2
    );
    return dist <= tolerance;
  }

  // Calculate projection parameter
  let t = ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));

  // Calculate nearest point on line segment
  const nearestX = lineStart.x + t * dx;
  const nearestY = lineStart.y + t * dy;

  // Calculate distance to nearest point
  const dist = Math.sqrt((point.x - nearestX) ** 2 + (point.y - nearestY) ** 2);

  return dist <= tolerance;
}

/**
 * Check if path crosses curved walls (for new wall segment system)
 */
export function isPathCrossingCurvedWalls(
  prevPos: Position,
  currentPos: Position,
  wallSegments: WallSegment[]
): boolean {
  for (const wall of wallSegments) {
    if (wall.type === 'line') {
      // Line-line intersection
      if (lineSegmentsIntersect(prevPos, currentPos, wall.start, wall.end)) {
        return true;
      }
    } else if (wall.type === 'bezier' && wall.control1) {
      // Line-bezier intersection
      if (lineIntersectsBezierCurve(prevPos, currentPos, wall.start, wall.end, wall.control1)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if two line segments intersect
 */
function lineSegmentsIntersect(
  p1: Position,
  p2: Position,
  p3: Position,
  p4: Position
): boolean {
  const ccw = (a: Position, b: Position, c: Position) => {
    return (c.y - a.y) * (b.x - a.x) > (b.y - a.y) * (c.x - a.x);
  };

  return ccw(p1, p3, p4) !== ccw(p2, p3, p4) && ccw(p1, p2, p3) !== ccw(p1, p2, p4);
}
